import React, { useEffect, useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ConfigProvider, Layout, Menu, Avatar, Dropdown, Tag, Space, theme } from 'antd'
import type { MenuProps } from 'antd'
import {
  LeftOutlined,
  RightOutlined,
  LogoutOutlined,
  DownOutlined,
  CalendarOutlined,
  SettingOutlined,
  DatabaseOutlined,
  AuditOutlined,
  MessageOutlined,
  LineChartOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { useTenantConfig } from '@/contexts/TenantConfigContext'
import { buildMenuTree, filterMenu, MENU_DEFINITIONS } from './menuConfig'
import type { MenuItemDef } from './menuConfig'
import { menuApi } from '@/shared/api/apiEndpoints'
// 하이볼 공식 로고 — 펼침 시 와이드 워드마크, 접힘 시 'Hi' 심볼(워드마크에서 잘라낸 정사각).
import logoWide from '@/assets/images/logo_hiball.png'
import logoSquare from '@/assets/images/logo_hiball_symbol.png'
import AppFooter from './AppFooter'
import type { UserLevel } from '@/types'

const { Header, Sider, Content } = Layout

// 셸(사이드바)의 색 토큰 — 본문(페이지)은 기존 antd 라이트 톤 유지.
//
// 이 시스템은 특정 구단이 아니라 하이볼이 여러 구단의 데이터를 관리하는 도구다.
// 그래서 팔레트 전체를 하이볼 공식 로고색(남색 #20489D) 계열로 맞춘다.
// 구조는 참조 프로젝트 giants_trackman_analyzer의 `siderTheme.ts`와 같다 —
// "배경은 어둡게 + 브랜드색을 선택 강조로 + 글자는 흐린 톤 → hover 흰색".
//   이전(한화 유래): 웜 블랙 배경(#17120F) + 오렌지 강조(#FC4E00) + 웜그레이 글자(#A8A29E)
//   현재(하이볼)   : 네이비 그래파이트 배경(#111520) + 로고 남색 강조(#20489D) + 쿨그레이 글자(#98A2B3)
// 선택 항목은 "액센트를 배경으로 깔고 글자를 흰색"으로 둔다 — 어두운 배경 위의 남색 글씨보다
// 대비가 훨씬 좋다.
const SIDER_BG = 'linear-gradient(180deg, #111520 0%, #1A2030 100%)'
/** 그라데이션을 쓸 수 없는 곳(접힌 상태의 서브메뉴 팝업 등)에 쓰는 단색 */
const SIDER_BG_SOLID = '#151A26'
/** 한 단계 밝은 배경 — 시스템 전환기 버튼 등 (참조의 SIDER_BG_ELEVATED 상당) */
const SIDER_BG_ELEVATED = '#242C3D'
/** 구분선 */
const SIDER_DIVIDER = '#242C3D'
/** hover 배경 — 어두운 배경 위에 흰색을 옅게 덮는다 */
const SIDER_HOVER_BG = 'rgba(255, 255, 255, 0.08)'
/** 어두운 배경 위에 올라가는 글자색 (네이비 배경에 맞춘 쿨그레이) */
const ON_SIDER = '#98A2B3'
const ON_SIDER_STRONG = '#FFFFFF'
const ON_SIDER_DIM = '#6B7688'
/**
 * 선택된 메뉴 = 브랜드 남색 배경 + 흰 글자.
 * 로고색 그대로면 어두운 배경과 명도가 붙어 알약이 안 보이므로 한 단계 밝은 값을 쓴다.
 */
const SIDER_SELECTED_BG = '#2B5CC0'
const SIDER_SELECTED_COLOR = '#FFFFFF'
/** 하이볼 브랜드 남색(로고와 같은 값) — 라이트 배경(헤더) 위에서 쓰는 값 */
const ACCENT = '#20489D'
/**
 * 로고 판 — 공식 로고가 진한 남색(#20489D)이라 어두운 사이드바 위에서는 거의 안 보인다.
 * 로고 색을 고치는 대신 흰 판을 깔아 원본 색을 그대로 살린다.
 */
const LOGO_PLATE_BG = '#FFFFFF'
const LOGO_PLATE_RADIUS = 6
const LOGO_WIDE_HEIGHT = 22
const LOGO_SYMBOL_SIZE = 22
const LOGO_PLATE_COLLAPSED_SIZE = 32

const SIDER_WIDTH = 240
const SIDER_COLLAPSED_WIDTH = 64
const SHELL_BAR_HEIGHT = 56

const ICON_MAP: Record<string, React.ReactNode> = {
  calendar:  <CalendarOutlined />,
  setting:   <SettingOutlined />,
  database:  <DatabaseOutlined />,
  audit:     <AuditOutlined />,
  message:   <MessageOutlined />,
  lineChart: <LineChartOutlined />,
  dollar:    <DollarCircleOutlined />,
}

function buildAntMenuItems(menus: MenuItemDef[]): MenuProps['items'] {
  return menus.map((m) => ({
    key: m.key,
    icon: m.icon ? ICON_MAP[m.icon] : undefined,
    label: m.children?.length ? m.label : <Link to={m.path}>{m.label}</Link>,
    children: m.children?.length ? buildAntMenuItems(m.children) : undefined,
  }))
}

/** 메뉴 트리를 평면화 (선택 키 계산 / key → path 매핑용) */
function flattenMenuDefs(defs: MenuItemDef[]): MenuItemDef[] {
  return defs.flatMap((d) => [d, ...(d.children ? flattenMenuDefs(d.children) : [])])
}

const USER_LEVEL_COLORS: Record<number, string> = {
  0: 'red',
  4: 'blue',
  5: 'green',
  9: 'default',
}
const USER_LEVEL_LABELS: Record<number, string> = {
  0: '최고관리자',
  4: '기록담당',
  5: '조회담당',
  9: '일반',
}

const MainLayout: React.FC = () => {
  const { user, logout } = useAuth()
  const { siteOwner, modules, functions } = useTenantConfig()
  // 화면 폭과 무관하게 항상 접힌 상태로 시작한다 (본문에 폭을 최대한 준다).
  const [collapsed, setCollapsed] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const { token } = theme.useToken()

  // 메뉴는 권한 DB에서 받아온다 (GET /v1/menu-list). 실패하거나 빈 목록이면 정적 폴백을 그대로 쓴다.
  const [dbMenus, setDbMenus] = useState<MenuItemDef[] | null>(null)
  useEffect(() => {
    let cancelled = false
    menuApi
      .list()
      .then((menus) => {
        if (cancelled || menus.length === 0) return
        setDbMenus(buildMenuTree(menus))
      })
      .catch((err) => {
        console.error('메뉴 목록 조회 실패 - 정적 폴백 목록을 사용합니다', err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // 권한 DB 메뉴는 이미 사용자 권한으로 걸러진 결과라 filterMenu를 다시 태우지 않는다.
  // 폴백 목록만 기존처럼 레벨/모듈/기능 기준으로 거른다.
  const menuDefs = useMemo(() => {
    if (dbMenus) return dbMenus
    if (!user) return []
    return filterMenu(MENU_DEFINITIONS, user.userLevel as UserLevel, modules, functions)
  }, [dbMenus, user, modules, functions])

  const menuItems = useMemo(() => buildAntMenuItems(menuDefs), [menuDefs])

  // 메뉴 key(DB 메뉴면 menuId, 폴백이면 정적 key) → 이동 경로
  const pathByKey = useMemo(
    () => new Map(flattenMenuDefs(menuDefs).map((d) => [d.key, d.path])),
    [menuDefs],
  )

  // 현재 경로에 맞는 메뉴 키 계산 — 하위 경로(상세 화면 등)에서도 해당 메뉴가 선택돼 보이도록
  // 가장 길게 일치하는 경로를 고른다.
  const selectedKeys = useMemo(() => {
    const path = location.pathname
    const matched = flattenMenuDefs(menuDefs)
      .filter((d) => path === d.path || path.startsWith(`${d.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0]
    return matched ? [matched.key] : []
  }, [location.pathname, menuDefs])

  // 현재 경로의 상위 메뉴 펼침
  const openKeys = useMemo(() => {
    const path = location.pathname
    const parent = menuDefs.find((m) => m.children?.some((c) => path.startsWith(c.path)))
    return parent ? [parent.key] : []
  }, [location.pathname, menuDefs])

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      onClick: () => logout().then(() => navigate('/login')),
    },
  ]

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* 사이드바 내부 3단 세로 배치(로고 / 메뉴 / 로그아웃)는 antd가 감싸는
          `.ant-layout-sider-children`에 flex를 걸어야 동작한다 — index.css의 `.app-sider` 규칙 참고 */}
      <Sider
        className="app-sider"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={SIDER_WIDTH}
        collapsedWidth={SIDER_COLLAPSED_WIDTH}
        style={{ background: SIDER_BG }}
      >
        {/* 로고 블록 — 로고는 진한 남색이라 어두운 사이드바 위에 흰 판(LOGO_PLATE_BG)을 깔아
            읽히게 한다. 접힌 상태에서는 로고 클릭이 사이드바를 펼치는 트리거가 된다. */}
        <div
          style={{
            padding: collapsed ? 0 : '11px 16px',
            borderBottom: `1px solid ${SIDER_DIVIDER}`,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <div
            onClick={() => collapsed && setCollapsed(false)}
            style={{
              height: collapsed ? SHELL_BAR_HEIGHT : 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              cursor: collapsed ? 'pointer' : 'default',
            }}
            title={collapsed ? '사이드 메뉴 열기' : siteOwner}
          >
            {collapsed ? (
              <div
                style={{
                  width: LOGO_PLATE_COLLAPSED_SIZE,
                  height: LOGO_PLATE_COLLAPSED_SIZE,
                  borderRadius: LOGO_PLATE_RADIUS,
                  background: LOGO_PLATE_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={logoSquare}
                  alt={siteOwner}
                  style={{ width: LOGO_SYMBOL_SIZE, height: LOGO_SYMBOL_SIZE, display: 'block' }}
                />
              </div>
            ) : (
              // 폭이 아니라 높이 기준으로 맞춘다 — 폭 기준이면 워드마크 위아래가 잘린다.
              <div
                style={{
                  borderRadius: LOGO_PLATE_RADIUS,
                  background: LOGO_PLATE_BG,
                  padding: '6px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                }}
              >
                <img
                  src={logoWide}
                  alt={siteOwner}
                  style={{
                    height: LOGO_WIDE_HEIGHT, width: 'auto', maxWidth: '100%', display: 'block',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* 메뉴 — 사이드바만 다크 팔레트를 쓰므로 ConfigProvider를 여기에만 국소 적용한다.
            antd의 `theme="dark"` 토큰 세트를 쓰는 이유는 "어두운 배경"이 아니라 "배경이 진하고 글자가
            밝은" 구성이 필요해서다 — 남색 배경도 여기에 해당한다. */}
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                darkItemBg: 'transparent',
                darkSubMenuItemBg: 'transparent',
                darkItemColor: ON_SIDER,
                darkItemHoverBg: SIDER_HOVER_BG,
                darkItemHoverColor: ON_SIDER_STRONG,
                darkItemSelectedBg: SIDER_SELECTED_BG,
                darkItemSelectedColor: SIDER_SELECTED_COLOR,
                // 접힌 상태에서 뜨는 서브메뉴 팝업 — 그라데이션은 못 쓰므로 단색으로
                darkPopupBg: SIDER_BG_SOLID,
                itemBorderRadius: 8,
                itemMarginInline: 8,
                itemHeight: 42,
              },
            },
          }}
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKeys}
            defaultOpenKeys={openKeys}
            items={menuItems}
            style={{ background: 'transparent', borderRight: 0, marginTop: 8, flex: 1, overflowY: 'auto' }}
            onClick={({ key }) => {
              const path = pathByKey.get(key)
              if (path) navigate(path)
            }}
          />
        </ConfigProvider>

        {/* 로그아웃 — 레퍼런스처럼 사이드바 최하단 고정 */}
        <div style={{ borderTop: `1px solid ${SIDER_DIVIDER}`, padding: '12px 8px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => logout().then(() => navigate('/login'))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: collapsed ? '10px 0' : '10px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: 8,
              background: 'transparent',
              border: 'none',
              color: ON_SIDER_DIM,
              cursor: 'pointer',
              fontSize: 14,
              boxSizing: 'border-box',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = SIDER_HOVER_BG
              e.currentTarget.style.color = ON_SIDER_STRONG
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = ON_SIDER_DIM
            }}
            title="로그아웃"
          >
            <LogoutOutlined style={{ fontSize: collapsed ? 18 : 16, flexShrink: 0 }} />
            {!collapsed && <span>로그아웃</span>}
          </button>
        </div>
      </Sider>

      {/* 사이드메뉴 열고/닫는 원형 버튼 — 사이드바 가장자리에 반쯤 걸치게 배치(레퍼런스 동일) */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? '메뉴 펼치기' : '메뉴 접기'}
        style={{
          position: 'fixed',
          top: SHELL_BAR_HEIGHT / 2 - 13,
          left: collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH,
          transform: 'translateX(-50%)',
          transition: 'left 0.2s',
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '2px solid #F3F4F6',
          background: SIDER_BG_ELEVATED,
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 110,
          color: ON_SIDER,
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = ACCENT
          e.currentTarget.style.color = '#fff'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = SIDER_BG_ELEVATED
          e.currentTarget.style.color = ON_SIDER
        }}
      >
        {collapsed ? <RightOutlined style={{ fontSize: 10 }} /> : <LeftOutlined style={{ fontSize: 10 }} />}
      </button>

      <Layout style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header
          style={{
            // 좌측 패딩은 사이드바 가장자리 원형 토글 버튼(폭 26px의 절반)을 피하도록 넉넉히 준다.
            padding: '0 16px 0 26px',
            height: SHELL_BAR_HEIGHT,
            lineHeight: `${SHELL_BAR_HEIGHT}px`,
            background: token.colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            // 시스템 스위처는 레퍼런스처럼 사이드바(로고 아래)로 옮겼으므로 헤더에는 사용자 정보만 남는다.
            justifyContent: 'flex-end',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            flexShrink: 0,
          }}
        >
          {/* 우측: 로그인 사용자 */}
          <Space size={8}>
            {user && (
              <Tag color={USER_LEVEL_COLORS[user.userLevel] ?? 'default'} style={{ marginInlineEnd: 0 }}>
                {USER_LEVEL_LABELS[user.userLevel] ?? `Level ${user.userLevel}`}
              </Tag>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 8px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <Avatar size={28} style={{ background: ACCENT, flexShrink: 0 }}>
                  {user?.name?.charAt(0)}
                </Avatar>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{user?.name}</span>
                <DownOutlined style={{ fontSize: 10, color: '#9CA3AF' }} />
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            padding: 24,
            background: token.colorBgLayout,
            flex: 1,
            overflow: 'auto',
            height: 0,
          }}
        >
          <Outlet />
        </Content>

        <AppFooter />
      </Layout>
    </Layout>
  )
}

export default MainLayout
